// Pure helpers for the merchant Home (design M:350-847, PRD §7.3.3 row 1): KPI figures
// from the insights slice (API-123) with a bookings-list fallback when the plan has no
// insights entitlement, the 30-day chart geometry (the design's own spline), the
// attention strip rules, schedule counts and share links. No React, no I/O — tested
// directly (home-model.test.ts). No brand or location literals (INF-1).
import type { Business, Insights, MerchantBooking } from '../../mock/seeds/schema-types';

// ---- period switcher (design `periods`: Yesterday · 7 days · 30 days · 12 months) ----
export const PERIODS = ['1d', '7d', '30d', '12m'] as const;
export type Period = (typeof PERIODS)[number];
/** Days a period covers — the bookings-list fallback window. */
export const PERIOD_DAYS: Record<Period, number> = { '1d': 1, '7d': 7, '30d': 30, '12m': 365 };

export type GreetingPart = 'morning' | 'afternoon' | 'evening';
export function greetingPart(hour: number): GreetingPart {
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

// ---- business names ----
// A multi-branch business is named "<brand> — <branch>"; the design shows the branch
// alone on tabs, tags and cards and the brand as the card's sub line.
const BRANCH_SEP = /\s*—\s*/;
export function branchLabel(name: string): string {
  const parts = name.split(BRANCH_SEP);
  return parts.length > 1 ? parts.slice(1).join(' — ') : name;
}
export function brandLabel(name: string): string {
  const parts = name.split(BRANCH_SEP);
  return parts.length > 1 ? parts[0]! : name;
}
export function businessName(b: Pick<Business, 'name' | 'name_bn'>, lang: 'en' | 'bn'): string {
  return lang === 'bn' && b.name_bn ? b.name_bn : b.name;
}

// ---- KPIs ----
export interface Trend {
  /** Percent change (bookings, completed, revenue) or absolute delta (rating). */
  value: number;
  kind: 'pct' | 'delta';
  up: boolean;
}
export interface KpiFigures {
  bookings: number;
  cancelled: number;
  completed: number;
  completionPct: number;
  revenueMinor: number;
  avgMinor: number;
  rating: number | null;
  reviews: number;
  trends: { bookings: Trend | null; completed: Trend | null; revenue: Trend | null; rating: Trend | null };
}

type Slice = { bookings?: number; revenue_minor?: number; completion_pct?: number; cancel_pct?: number; rating_avg?: number };

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}
function pctTrend(cur: number, prev: number | undefined): Trend | null {
  if (prev === undefined || prev <= 0) return null;
  const value = Math.round(((cur - prev) / prev) * 100);
  return { value: Math.abs(value), kind: 'pct', up: value >= 0 };
}
function completedOf(s: Slice): number {
  return Math.round(((s.bookings ?? 0) * (s.completion_pct ?? 0)) / 100);
}

/** KPI card figures from an insights slice; `reviews` is the businesses' review count. */
export function kpisFromInsights(ins: Insights, reviews: number): KpiFigures {
  const bookings = ins.bookings ?? 0;
  const completed = completedOf(ins);
  const revenueMinor = ins.revenue_minor ?? 0;
  const prevRaw = (ins.prev ?? {}) as Record<string, unknown>;
  const prev: Slice = {
    bookings: num(prevRaw.bookings),
    revenue_minor: num(prevRaw.revenue_minor),
    completion_pct: num(prevRaw.completion_pct),
    rating_avg: num(prevRaw.rating_avg),
  };
  const rating = ins.rating_avg ?? null;
  const ratingDelta =
    rating !== null && prev.rating_avg !== undefined ? Math.round((rating - prev.rating_avg) * 10) / 10 : undefined;
  return {
    bookings,
    cancelled: Math.round((bookings * (ins.cancel_pct ?? 0)) / 100),
    completed,
    completionPct: bookings > 0 ? Math.round((completed / bookings) * 100) : 0,
    revenueMinor,
    avgMinor: avgPerBooking(revenueMinor, completed),
    rating,
    reviews,
    trends: {
      bookings: pctTrend(bookings, prev.bookings),
      completed: prev.bookings !== undefined && prev.completion_pct !== undefined ? pctTrend(completed, completedOf(prev)) : null,
      revenue: pctTrend(revenueMinor, prev.revenue_minor),
      rating: ratingDelta === undefined ? null : { value: Math.abs(ratingDelta), kind: 'delta', up: ratingDelta >= 0 },
    },
  };
}

const COMPLETED: readonly string[] = ['completed'];
const CANCELLED: readonly string[] = ['cancelled', 'rejected', 'expired', 'refunded'];
const EARNING: readonly string[] = ['confirmed', 'in_progress', 'completed'];

/**
 * Fallback when the plan lacks the insights entitlement (403, DR-31): the same cards
 * computed from the bookings list over the period window. No previous-window trend.
 */
export function kpisFromBookings(rows: readonly MerchantBooking[], rating: number | null, reviews: number): KpiFigures {
  const bookings = rows.length;
  const completed = rows.filter((b) => COMPLETED.includes(b.status)).length;
  const revenueMinor = rows.filter((b) => EARNING.includes(b.status)).reduce((a, b) => a + (b.total_minor ?? 0), 0);
  return {
    bookings,
    cancelled: rows.filter((b) => CANCELLED.includes(b.status)).length,
    completed,
    completionPct: bookings > 0 ? Math.round((completed / bookings) * 100) : 0,
    revenueMinor,
    avgMinor: avgPerBooking(revenueMinor, completed),
    rating,
    reviews,
    trends: { bookings: null, completed: null, revenue: null, rating: null },
  };
}

/** Average per completed booking, rounded to whole major units (100 minor). */
export function avgPerBooking(revenueMinor: number, completed: number, scale = 100): number {
  return Math.round(revenueMinor / Math.max(completed, 1) / scale) * scale;
}

/** Weighted rating across businesses and their summed review count. */
export function ratingOf(businesses: readonly Pick<Business, 'rating' | 'review_count'>[]): { rating: number | null; reviews: number } {
  const reviews = businesses.reduce((a, b) => a + (b.review_count ?? 0), 0);
  if (reviews === 0) return { rating: null, reviews: 0 };
  const sum = businesses.reduce((a, b) => a + (b.rating ?? 0) * (b.review_count ?? 0), 0);
  return { rating: Math.round((sum / reviews) * 10) / 10, reviews };
}

// ---- chart ----
export const CHART_W = 640;
export const CHART_H = 120;

/** The design's Catmull-Rom-ish spline (M:10607): tension 0.22, one decimal. */
export function spline(pts: readonly (readonly [number, number])[]): string {
  if (pts.length < 2) return pts.length ? `M${pts[0]![0]} ${pts[0]![1]}` : '';
  let d = `M${pts[0]![0].toFixed(1)} ${pts[0]![1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[i + 2] ?? p2;
    const t = 0.22;
    const c1 = [p1[0] + (p2[0] - p0[0]) * t, p1[1] + (p2[1] - p0[1]) * t];
    const c2 = [p2[0] - (p3[0] - p1[0]) * t, p2[1] - (p3[1] - p1[1]) * t];
    d += ` C${c1[0]!.toFixed(1)} ${c1[1]!.toFixed(1)} ${c2[0]!.toFixed(1)} ${c2[1]!.toFixed(1)} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return d;
}

/** y of a value in the chart box: the top 14 units stay free (design pathOf). */
export function chartY(v: number, max: number, h = CHART_H): number {
  return h - (v / Math.max(max, 1)) * (h - 14);
}

export function linePath(values: readonly number[], max: number, w = CHART_W, h = CHART_H): string {
  const n = values.length;
  return spline(values.map((v, i) => [n === 1 ? 0 : i * (w / (n - 1)), chartY(v, max, h)] as const));
}

export function areaPath(line: string, w = CHART_W, h = CHART_H): string {
  return line ? `M0 ${h} ${line.slice(1)} L${w} ${h} Z` : '';
}

/** Axis ticks top→bottom (design chartTicks: max × 1, .75, .5, .25, 0). */
export function chartTicks(max: number): number[] {
  return [1, 0.75, 0.5, 0.25, 0].map((r) => Math.round(max * r));
}

export interface SeriesStats {
  total: number;
  avg: number;
  best: number;
  bestIndex: number;
}
export function seriesStats(values: readonly number[]): SeriesStats {
  const total = values.reduce((a, b) => a + b, 0);
  const best = values.length ? Math.max(...values) : 0;
  return { total, avg: values.length ? Math.round(total / values.length) : 0, best, bestIndex: values.indexOf(best) };
}

/** Indices of the four x-axis labels (first, ⅓, ⅔, last — design 1/10/20/30). */
export function axisLabelIndices(n: number): number[] {
  if (n <= 1) return [0];
  return [0, Math.floor((n - 1) / 3), Math.floor(((n - 1) * 2) / 3), n - 1];
}

export interface DaySeries {
  businessId?: string;
  days: string[];
  values: number[];
}

type SeriesPoint = { day?: string; value?: number };

/** Per-business daily series from `per_business_series` (API-123), else the total. */
export function seriesFromInsights(ins: Insights | undefined): DaySeries[] {
  if (!ins) return [];
  const per = (ins.per_business_series ?? []).map((s) => {
    const pts = ((s.series ?? []) as SeriesPoint[]).filter((p) => typeof p.day === 'string');
    return { businessId: s.business_id, days: pts.map((p) => p.day!), values: pts.map((p) => Number(p.value ?? 0)) };
  });
  if (per.length) return per;
  const pts = (ins.series ?? []).filter((p) => !p.metric || p.metric === 'bookings');
  return pts.length ? [{ days: pts.map((p) => p.day ?? ''), values: pts.map((p) => Number(p.value ?? 0)) }] : [];
}

/** Daily counts per business from a bookings list (fallback without insights). */
export function seriesFromBookings(rows: readonly MerchantBooking[], days: readonly string[], businessIds: readonly string[]): DaySeries[] {
  return businessIds.map((id) => ({
    businessId: id,
    days: [...days],
    values: days.map((d) => rows.filter((b) => b.business_id === id && localDay(b.starts_at) === d).length),
  }));
}

// ---- dates ----
export function localDay(iso: string | undefined | Date): string {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** Whole local calendar days from `a` to `b` (0 = same day). */
export function calendarDays(a: Date, b: Date): number {
  const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((ub - ua) / 86_400_000);
}
export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
/** The last `n` local days ending today, oldest first. */
export function lastDays(now: Date, n: number): string[] {
  return Array.from({ length: n }, (_, i) => localDay(addDays(now, i - n + 1)));
}

// ---- schedule ----
export const NEEDS_YOU: readonly string[] = ['pending', 'payment_verification'];
export const needsAction = (b: Pick<MerchantBooking, 'status'>): boolean => NEEDS_YOU.includes(b.status);
const LIVE: readonly string[] = ['pending', 'payment_verification', 'confirmed', 'in_progress'];

export function byStart(a: Pick<MerchantBooking, 'starts_at'>, b: Pick<MerchantBooking, 'starts_at'>): number {
  return (a.starts_at ?? '').localeCompare(b.starts_at ?? '');
}

export interface BusinessDay {
  count: number;
  revenueMinor: number;
  toReview: number;
  next: MerchantBooking | undefined;
}
/** A business card's figures from today's rows (design bizPreview). */
export function businessDay(rows: readonly MerchantBooking[], businessId: string): BusinessDay {
  const mine = rows.filter((b) => b.business_id === businessId).sort(byStart);
  return {
    count: mine.length,
    revenueMinor: mine.reduce((a, b) => a + (b.total_minor ?? 0), 0),
    toReview: mine.filter(needsAction).length,
    next: mine.find((b) => LIVE.includes(b.status)),
  };
}

// ---- attention strip (design `att`, M:10004-10012) ----
export type AttentionKind = 'proofs' | 'fix_service' | 'offer_ending' | 'kyc_review';
export interface AttentionItem {
  kind: AttentionKind;
  count: number;
  /** proofs: oldest age in hours · offer_ending: days left · kyc_review: business name */
  hours?: number;
  days?: number;
  businessId?: string;
  itemId?: string;
}
export const ATTENTION_PAGE = 4;
/** An active offer "is ending" when it ends within this many days. */
export const OFFER_ENDING_DAYS = 7;

export function attentionItems(input: {
  proofQueue: readonly MerchantBooking[];
  services: readonly { id: string; status?: string; business_id: string }[];
  offers: readonly { status?: string; ends_at?: string }[];
  businesses: readonly Pick<Business, 'id' | 'payment_application_status'>[];
  now: Date;
}): AttentionItem[] {
  const out: AttentionItem[] = [];
  const { proofQueue, services, offers, businesses, now } = input;
  if (proofQueue.length) {
    const oldestMin = Math.max(...proofQueue.map((b) => b.sla_age_min ?? 0));
    out.push({ kind: 'proofs', count: proofQueue.length, hours: Math.floor(oldestMin / 60) });
  }
  const fix = services.filter((s) => s.status === 'changes_requested' || s.status === 'rejected');
  if (fix.length) out.push({ kind: 'fix_service', count: fix.length, itemId: fix[0]!.id, businessId: fix[0]!.business_id });
  const ending = offers
    .filter((o) => o.status === 'active' && o.ends_at)
    .map((o) => calendarDays(now, new Date(o.ends_at!)))
    .filter((d) => d >= 0 && d <= OFFER_ENDING_DAYS)
    .sort((a, b) => a - b);
  if (ending.length) out.push({ kind: 'offer_ending', count: ending.length, days: ending[0] });
  for (const b of businesses) {
    if (b.payment_application_status === 'under_review') {
      out.push({ kind: 'kyc_review', count: 1, businessId: b.id });
    }
  }
  return out;
}

// ---- share link ----
/** Public booking link: `${web_base_url}/b/{slug}` (API-49), `?l=` scopes a service. */
export function shareUrl(webBase: string, slug: string, itemSlug?: string): string {
  const base = `${webBase.replace(/\/$/, '')}/b/${slug}`;
  return itemSlug ? `${base}?l=${encodeURIComponent(itemSlug)}` : base;
}
export function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

// ---- Free plan (DR-31) ----
export function addMonths(iso: string, months: number): Date {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + months);
  return d;
}
